import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { Phone, Menu, X, ChevronDown, Mail, Clock } from 'lucide-react';
import { services } from '../utilities/services';
import { motion } from 'framer-motion';

const Header: React.FC = () => {
    const [isMenuOpen, setIsMenuOpen] = useState(false);
    const [isScrolled, setIsScrolled] = useState(false);
    const [isServicesOpen, setIsServicesOpen] = useState(false);

    //const navigate = useNavigate();

    const serviceLinks = services.map(service => ({
        name: service.title,
        path: `/services/${service.id}`
    }));

    useEffect(() => {
        const handleScroll = () => {
            setIsScrolled(window.scrollY > 50);
        };

        window.addEventListener('scroll', handleScroll);
        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // 
    const TopBar = () => {
        const items = [
            {
                icon: <Clock size={14} />,
                text: "Mon - Sat: 09:00 AM - 05:00 PM"
            },
            {
                icon: <Mail size={14} />,
                text: "hirak.pahari@gmail.com"
            },
            {
                icon: <Phone size={14} />,
                text: "+917827039028",
                isLink: true
            }
        ];

        const containerVariants = {
            hidden: { opacity: 0 },
            visible: {
                opacity: 1,
                transition: {
                    duration: 0.5
                }
            }
        };

        return (
            <motion.div
                className="bg-brown text-white py-1.5 px-2 md:px-4 overflow-hidden"
                initial="hidden"
                animate="visible"
                variants={containerVariants}
            >
                <div className="container mx-auto">
                    <div className="overflow-hidden whitespace-nowrap">
                        <div className="inline-block animate-marquee">
                            {[...items, ...items].map((item, index) => (
                                <span
                                    key={index}
                                    className="inline-flex items-center mx-4 sm:mx-8 text-[10px] md:text-xs"
                                >
                                    <span className="mr-1.5">{item.icon}</span>
                                    {item.isLink ? (
                                        <a href={`tel:${item.text}`} className="hover:text-blue-200">
                                            {item.text}
                                        </a>
                                    ) : (
                                        <span>{item.text}</span>
                                    )}
                                    <span className="mx-2">/</span>
                                </span>
                            ))}
                        </div>
                    </div>
                </div>
            </motion.div>
        );
    };


    return (
        <>
            <TopBar />
            <header className={`sticky top-0 z-50 transition-all duration-300 ${isScrolled ? 'bg-white shadow-md' : 'bg-white'}`}>
                <div className="container mx-auto px-4">
                    <div className="flex justify-between items-center py-4">
                        <Link to="/" className="flex items-center space-x-3">
                            <span className="text-2xl font-bold text-darkbrown">Dr. Hirak Pahari</span>
                        </Link>

                        <nav className="hidden md:flex items-center space-x-8">
                            <NavLink to="/">Home</NavLink>
                            <NavLink to="/about">About</NavLink>

                            <div className="relative group">
                                <button className="flex items-center text-darkbrown hover:text-brown">
                                    Services
                                    <ChevronDown className="ml-1 h-4 w-4" />
                                </button>
                                <div className="absolute left-0 mt-2 w-64 rounded-md shadow-lg bg-white ring-1 ring-black ring-opacity-5 opacity-0 invisible group-hover:opacity-100 group-hover:visible transition-all duration-300">
                                    <div className="py-2">
                                        {serviceLinks.map((service) => (
                                            <Link
                                                key={service.path}
                                                to={service.path}
                                                className="block px-4 py-2 text-sm text-darkbrown hover:bg-blue-50 hover:text-brown"
                                            >
                                                {service.name}
                                            </Link>
                                        ))}
                                    </div>
                                </div>
                            </div>
                            <NavLink to='/media'>Gallery</NavLink>
                            <NavLink to='/blogs'>Blogs</NavLink>
                            <NavLink to='/contact'>Contact</NavLink>
                        </nav>

                        <button
                            className="md:hidden text-darkbrown"
                            onClick={() => setIsMenuOpen(!isMenuOpen)}
                        >
                            {isMenuOpen ? <X size={24} /> : <Menu size={24} />}
                        </button>
                    </div>

                    {/* Mobile menu */}
                    {isMenuOpen && (
                        <div className="md:hidden py-4">
                            <nav className="flex flex-col space-y-4">
                                <MobileNavLink to="/" onClick={() => setIsMenuOpen(false)}>Home</MobileNavLink>
                                <MobileNavLink to="/about" onClick={() => setIsMenuOpen(false)}>About</MobileNavLink>

                                <button
                                    className="text-left px-4 py-2 text-darkbrown hover:text-brown"
                                    onClick={() => setIsServicesOpen(!isServicesOpen)}
                                >
                                    <div className="flex items-center justify-between">
                                        <span>Services</span>
                                        <ChevronDown className={`transform transition-transform ${isServicesOpen ? 'rotate-180' : ''}`} />
                                    </div>
                                </button>

                                {isServicesOpen && (
                                    <div className="pl-8 space-y-2">
                                        {serviceLinks.map((service) => (
                                            <Link
                                                key={service.path}
                                                to={service.path}
                                                className="block py-2 text-darkbrown hover:text-brown"
                                                onClick={() => setIsMenuOpen(false)}
                                            >
                                                {service.name}
                                            </Link>
                                        ))}
                                    </div>
                                )}

                                <MobileNavLink to='/media' onClick={()=> setIsMenuOpen(false)}>Gallery</MobileNavLink>
                                <MobileNavLink to='/blogs' onClick={() => setIsMenuOpen(false)}>Blogs</MobileNavLink>
                                <MobileNavLink to="/contact" onClick={() => setIsMenuOpen(false)}>Contact</MobileNavLink>
                            </nav>
                        </div>
                    )}
                </div>
            </header>
        </>
    );
};

interface NavLinkProps {
    to: string;
    children: React.ReactNode;
    onClick?: () => void;
}

const NavLink: React.FC<NavLinkProps> = ({ to, children }) => (
    <Link
        to={to}
        className="text-darkbrown hover:text-brown transition-colors duration-200"
    >
        {children}
    </Link>
);

const MobileNavLink: React.FC<NavLinkProps> = ({ to, children, onClick }) => (
    <Link
        to={to}
        className="block px-4 py-2 text-darkbrown hover:text-brown"
        onClick={onClick}
    >
        {children}
    </Link>
);

export default Header;